import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { gaEvent } from "@/lib/track";
import { Howl } from "howler";
import { musicClient, platformClient } from "@/lib/musicApi";
import { getViewerKey } from "@/lib/viewerKey";
import i18n from "@/i18n";
import { earlyUntil, fmtEarlyDate } from "@/lib/earlyAccess";
import { useToast } from "@/contexts/ToastContext";
import { useConversionNudge } from "@/contexts/ConversionNudgeContext";

const AudioPlayerContext = createContext();

// playerUI: "hidden" | "fullscreen" | "inline" | "global"
export function AudioPlayerProvider({ children }) {
  const toast = useToast();
  const { triggerNudge } = useConversionNudge();
  const triggerRef = useRef(triggerNudge);
  useEffect(() => { triggerRef.current = triggerNudge; }, [triggerNudge]);
  const howlRef = useRef(null);
  const urlCacheRef = useRef({});
  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playerUI, setPlayerUI] = useState("hidden");
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const repeatRef = useRef(repeat);
  const shuffleRef = useRef(shuffle);
  const queueRef = useRef([]);
  const indexRef = useRef(0);
  const currentRef = useRef(null);
  const seenRef = useRef(false);   // per-track: has this listen been recorded as "seen"
  useEffect(() => { repeatRef.current = repeat; }, [repeat]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { indexRef.current = index; }, [index]);
  useEffect(() => { currentRef.current = current; }, [current]);

  // Progress ticker while playing. Also records a real "seen" listen — matching
  // the video rule: streamed past min(15s, 40% of the track). Once per track,
  // members and guests (guest via the anon device key), never for locked items.
  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const h = howlRef.current;
      if (!h || !h.playing()) return;
      const ct = h.seek() || 0;
      setProgress(ct);
      const cur = currentRef.current;
      if (cur && !seenRef.current && !cur.locked) {
        const d = h.duration() || cur.duration_sec || 0;
        const threshold = Math.min(15, d * 0.4) || 15;
        if (ct >= threshold) {
          seenRef.current = true;
          platformClient.post("/feed/seen", { content_type: "track", content_id: cur.id, vk: getViewerKey() }).catch(() => {});
        }
      }
    }, 500);
    return () => clearInterval(id);
  }, [isPlaying]);

  const _formatFromUrl = (url) => {
    const m = url.split("?")[0].match(/\.([a-z0-9]+)$/i);
    return m ? [m[1].toLowerCase()] : ["mp3"];
  };

  const _loadIndex = useCallback(async (q, i) => {
    const track = q[i];
    if (!track) return;
    // Skip a redundant reload of the track that is already loaded.
    if (howlRef.current && currentRef.current && currentRef.current.id === track.id) { setIndex(i); return; }
    let url;
    const cached = urlCacheRef.current[track.id];
    if (cached && Date.now() - cached.at < 6600 * 1000) {
      url = cached.url;
    } else {
      try {
        const { data } = await musicClient.get(`/tracks/${track.id}/stream`);
        url = data.url;
        urlCacheRef.current[track.id] = { url, at: Date.now() };
      } catch (e) {
        const reason = e?.response?.data?.detail?.reason;
        if (reason === "coming_soon") toast.error("This track isn't available yet.");
        else if (reason === "premium_required") toast.error("Available with Don", { description: "Upgrade to access this track.", action: { label: "Upgrade", onClick: () => window.location.assign("/pricing") } });
        else if (reason === "early_access") {
          const until = earlyUntil(track.early_access_until);
          toast.error(until ? i18n.t("commerce.earlyToastBoss", { date: fmtEarlyDate(until) }) : "Don members have early access. Available soon.");
        }
        else toast.error("Could not start playback.");
        return;
      }
    }
    if (howlRef.current) { howlRef.current.unload(); }
    const howl = new Howl({
      src: [url],
      html5: true,
      format: _formatFromUrl(url),
      onplay: () => { setIsPlaying(true); setDuration(howl.duration() || track.duration_sec || 0); },
      onpause: () => setIsPlaying(false),
      onend: () => {
        if (repeatRef.current) { howl.seek(0); howl.play(); return; }
        // Guest / non-subscriber finished a public, unlocked track: nudge (once per session, guarded in the context).
        if (track && track.visibility === "public" && !track.locked) triggerRef.current();
        _advance(1);
      },
      onloaderror: () => toast.error("Audio failed to load."),
    });
    howlRef.current = howl;
    setCurrent(track);
    currentRef.current = track;
    setIndex(i);
    indexRef.current = i;
    seenRef.current = false;
    setProgress(0);
    howl.play();
  }, [toast]);

  const _advance = useCallback((dir) => {
    const q = queueRef.current;
    if (!q.length) return;
    let next = indexRef.current + dir;
    if (shuffleRef.current && q.length > 1) next = Math.floor(Math.random() * q.length);
    if (next < 0) next = 0;
    if (next >= q.length) next = q.length - 1;
    if (q[next]) _loadIndex(q, next);
  }, [_loadIndex]);

  // opts.ui lets a caller choose the surface (default "fullscreen"). Search uses
  // { ui: "hidden" } to play a track inline with no visible player. Queue/controls
  // are otherwise unchanged — only the queue source and the surface differ.
  const playTrack = useCallback((track, list, startIndex, opts = {}) => {
    const q = list && list.length ? list : [track];
    const i = typeof startIndex === "number" ? startIndex : q.findIndex((t) => t.id === track.id);
    const idx = i >= 0 ? i : 0;
    if (q[idx]) gaEvent("play_track", { content_type: "track", content_id: String(q[idx].id), content_name: q[idx].title });
    setQueue(q);
    queueRef.current = q;
    setPlayerUI(opts.ui || "fullscreen");
    // Same track already loaded (e.g. paused, then play pressed on its row or detail page): resume it.
    // _loadIndex alone would skip the reload and leave it paused, a dead click.
    const h = howlRef.current;
    if (h && currentRef.current && q[idx] && currentRef.current.id === q[idx].id) {
      setIndex(idx); indexRef.current = idx;
      if (!h.playing()) h.play();
      return;
    }
    _loadIndex(q, idx);
  }, [_loadIndex]);

  const togglePlay = useCallback(() => {
    const h = howlRef.current;
    if (!h) return;
    if (h.playing()) h.pause();
    else h.play();
  }, []);

  const pause = useCallback(() => {
    const h = howlRef.current;
    if (h) { try { h.pause(); } catch {} }
    setIsPlaying(false);
  }, []);

  const next = useCallback(() => _advance(1), [_advance]);
  const prev = useCallback(() => {
    const h = howlRef.current;
    if (h && (h.seek() || 0) > 3) { h.seek(0); setProgress(0); return; }
    _advance(-1);
  }, [_advance]);

  const seek = useCallback((sec) => {
    const h = howlRef.current;
    if (h) { h.seek(sec); setProgress(sec); }
  }, []);

  const dismiss = useCallback(() => {
    if (howlRef.current) { howlRef.current.unload(); howlRef.current = null; }
    setIsPlaying(false);
    setCurrent(null);
    setPlayerUI("hidden");
  }, []);

  // ── Manual queue controls (auto-queue via playTrack is untouched) ──
  const _syncIndexToCurrent = useCallback((nq) => {
    const curId = currentRef.current ? currentRef.current.id : null;
    const ni = curId ? nq.findIndex((t) => t.id === curId) : indexRef.current;
    const safe = ni >= 0 ? ni : 0;
    setIndex(safe); indexRef.current = safe;
  }, []);

  // Insert a track to play immediately after the current one.
  const playNext = useCallback((track) => {
    const q = queueRef.current;
    if (!q.length || !currentRef.current) { playTrack(track); return; }
    if (currentRef.current.id === track.id) return;
    const filtered = q.filter((t) => t.id !== track.id);
    let curIdx = filtered.findIndex((t) => t.id === currentRef.current.id);
    if (curIdx < 0) curIdx = indexRef.current;
    const nq = [...filtered.slice(0, curIdx + 1), track, ...filtered.slice(curIdx + 1)];
    setQueue(nq); queueRef.current = nq;
    setIndex(curIdx); indexRef.current = curIdx;
    toast.success("Playing next");
  }, [playTrack, toast]);

  // Append a track to the end of the queue.
  const addToQueue = useCallback((track) => {
    const q = queueRef.current;
    if (!q.length || !currentRef.current) { playTrack(track); return; }
    if (q.some((t) => t.id === track.id)) { toast.success("Already in queue"); return; }
    const nq = [...q, track];
    setQueue(nq); queueRef.current = nq;
    toast.success("Added to queue");
  }, [playTrack, toast]);

  // Remove a track from the queue by id. Removing the current track advances playback.
  const removeFromQueue = useCallback((id) => {
    const q = queueRef.current;
    const idx = q.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const isCurrent = currentRef.current && currentRef.current.id === id;
    const nq = q.filter((t) => t.id !== id);
    if (!nq.length) { dismiss(); return; }
    setQueue(nq); queueRef.current = nq;
    if (isCurrent) { _loadIndex(nq, Math.min(idx, nq.length - 1)); return; }
    _syncIndexToCurrent(nq);
  }, [dismiss, _loadIndex, _syncIndexToCurrent]);

  // Replace the queue order (drag-to-reorder). Keeps the current track's pointer.
  const reorderQueue = useCallback((nq) => {
    setQueue(nq); queueRef.current = nq;
    _syncIndexToCurrent(nq);
  }, [_syncIndexToCurrent]);

  // Jump to a specific track already in the queue.
  const jumpToQueueIndex = useCallback((i) => {
    const q = queueRef.current;
    if (q[i]) _loadIndex(q, i);
  }, [_loadIndex]);

  // Clear everything in the queue except the current track (Phase D).
  const clearUpNext = useCallback(() => {
    const cur = currentRef.current;
    if (!cur) return;
    const nq = [cur];
    setQueue(nq); queueRef.current = nq;
    setIndex(0); indexRef.current = 0;
    toast.success("Cleared up next");
  }, [toast]);

  // Play a track immediately (rail): insert right after current, then load it.
  const playNow = useCallback((track) => {
    const q = queueRef.current;
    if (!q.length || !currentRef.current) { playTrack(track); return; }
    const filtered = q.filter((t) => t.id !== track.id);
    let curIdx = filtered.findIndex((t) => t.id === currentRef.current.id);
    if (curIdx < 0) curIdx = indexRef.current;
    const nq = [...filtered.slice(0, curIdx + 1), track, ...filtered.slice(curIdx + 1)];
    setQueue(nq); queueRef.current = nq;
    _loadIndex(nq, curIdx + 1);
  }, [playTrack, _loadIndex]);

  const value = {
    current, queue, index, isPlaying, progress, duration, playerUI,
    shuffle, repeat, playTrack, togglePlay, pause, next, prev, seek, dismiss,
    playNext, addToQueue, removeFromQueue, reorderQueue, jumpToQueueIndex,
    clearUpNext, playNow,
    setPlayerUI, toggleShuffle: () => setShuffle((s) => !s), toggleRepeat: () => setRepeat((r) => !r),
  };
  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
}

export const useAudioPlayer = () => useContext(AudioPlayerContext);
